/**
 * Utility functions for working with Strapi CMS
 */

import type { BlocksContent } from 'vue-strapi-blocks-renderer'

/**
 * Converts a Strapi media URL to a full URL
 * @param url - The URL from Strapi (can be relative or absolute)
 * @returns Full URL with Strapi base URL if needed
 */
export const getStrapiImageUrl = (url: string): string => {
  return url.startsWith('http') ? url : `${import.meta.env.VITE_STRAPI_URL}${url}`
}

/**
 * Picks a resized version of a Strapi image, falling back to the original
 * @param image - Media object with its original url and optional formats
 * @param preferredFormats - Format names in order of preference (e.g. ['small', 'medium'])
 * @returns Full URL of the first available preferred format, or of the original
 */
export const getStrapiImageFormatUrl = (
  image: { url: string; formats?: Record<string, { url: string }> | null },
  preferredFormats: string[],
): string => {
  for (const format of preferredFormats) {
    const formatUrl = image.formats?.[format]?.url
    if (formatUrl) return getStrapiImageUrl(formatUrl)
  }
  return getStrapiImageUrl(image.url)
}

/**
 * Extracts plain text from Strapi rich text format for description fields
 * @param richText - Rich text content from Strapi description field
 * @returns Plain text string
 */
export const getDescriptionText = (richText: unknown): string => {
  if (!richText) return ''
  
  // If it's already a string, return it
  if (typeof richText === 'string') {
    return richText
  }
  
  // Handle Strapi rich text format array
  if (Array.isArray(richText)) {
    return richText.map(block => {
      if (block.children && Array.isArray(block.children)) {
        return extractPlainTextFromChildren(block.children)
      }
      return ''
    }).join(' ')
  }
  
  return ''
}

/**
 * Converts Strapi blocks content to plain text string for form fields
 * Handles both string and BlocksContent formats
 * @param description - Content from Strapi (string, blocks, or unknown)
 * @returns Plain text string suitable for form inputs
 */
export const convertBlocksToString = (description: string | BlocksContent | unknown): string => {
  if (!description) return ''
  if (typeof description === 'string') return description
  if (Array.isArray(description)) {
    // Convert Strapi blocks to plain text (simplified extraction)
    return description.map(block => {
      if (block && typeof block === 'object' && 'type' in block && block.type === 'paragraph') {
        // Try to extract text from paragraph blocks
        if ('children' in block && Array.isArray(block.children)) {
          return block.children.map((child: unknown) => {
            return (child && typeof child === 'object' && 'text' in child) ? (child as { text: string }).text : ''
          }).join('')
        }
      }
      return ''
    }).filter(text => text.length > 0).join('\n')
  }
  return String(description)
}

/**
 * Extract plain text from children nodes recursively
 * @param children - Array of text nodes
 * @returns Plain text string
 */
function extractPlainTextFromChildren(children: unknown[]): string {
  return children.map((child) => {
    const node = child as { text?: string; children?: unknown[] }
    if (node.text) {
      return node.text
    }
    if (node.children && Array.isArray(node.children)) {
      return extractPlainTextFromChildren(node.children)
    }
    return ''
  }).join('')
}


/**
 * Strapi "blocks" rich text, as stored for fields of type `blocks`
 */
type BlockTextNode = {
  type: 'text'
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  strikethrough?: boolean
  code?: boolean
}
type BlockLinkNode = { type: 'link'; url: string; children: BlockTextNode[] }
type BlockInlineNode = BlockTextNode | BlockLinkNode
export type StrapiBlockNode =
  | { type: 'paragraph'; children: BlockInlineNode[] }
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: BlockInlineNode[] }
  | { type: 'quote'; children: BlockInlineNode[] }
  | { type: 'code'; children: BlockTextNode[] }
  | { type: 'list'; format: 'ordered' | 'unordered'; children: { type: 'list-item'; children: BlockInlineNode[] }[] }

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const inlineToHtml = (nodes: unknown[] = []): string =>
  nodes
    .map((raw) => {
      const node = raw as BlockInlineNode
      if (node?.type === 'link') return `<a href="${escapeHtml(node.url || '')}">${inlineToHtml(node.children)}</a>`
      if (node?.type !== 'text') return ''
      let html = escapeHtml(node.text || '').replace(/\n/g, '<br>')
      if (node.code) html = `<code>${html}</code>`
      if (node.bold) html = `<strong>${html}</strong>`
      if (node.italic) html = `<em>${html}</em>`
      if (node.underline) html = `<u>${html}</u>`
      if (node.strikethrough) html = `<s>${html}</s>`
      return html
    })
    .join('')

/**
 * Converts Strapi blocks content to HTML for the rich text editor.
 * Strings (older HTML/plain values) are returned unchanged.
 */
export const blocksToHtml = (content: unknown): string => {
  if (!content) return ''
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return ''
  return content
    .map((raw) => {
      const block = raw as StrapiBlockNode
      switch (block?.type) {
        case 'heading':
          return `<h${block.level}>${inlineToHtml(block.children)}</h${block.level}>`
        case 'quote':
          return `<blockquote>${inlineToHtml(block.children)}</blockquote>`
        case 'code':
          return `<pre><code>${inlineToHtml(block.children)}</code></pre>`
        case 'list': {
          const tag = block.format === 'ordered' ? 'ol' : 'ul'
          return `<${tag}>${(block.children || []).map((item) => `<li>${inlineToHtml(item.children)}</li>`).join('')}</${tag}>`
        }
        case 'paragraph':
          return `<p>${inlineToHtml(block.children)}</p>`
        default:
          return ''
      }
    })
    .join('')
}

type Marks = Omit<BlockTextNode, 'type' | 'text'>

const MARK_TAGS: Record<string, keyof Marks> = {
  STRONG: 'bold',
  B: 'bold',
  EM: 'italic',
  I: 'italic',
  U: 'underline',
  S: 'strikethrough',
  DEL: 'strikethrough',
  STRIKE: 'strikethrough',
  CODE: 'code',
}

const collectInline = (node: Node, marks: Marks, out: BlockInlineNode[]): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || '').replace(/\s+/g, ' ')
    if (text) out.push({ type: 'text', text, ...marks })
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return
  const el = node as HTMLElement
  if (el.tagName === 'BR') {
    out.push({ type: 'text', text: '\n', ...marks })
    return
  }
  if (el.tagName === 'A') {
    const children: BlockInlineNode[] = []
    el.childNodes.forEach((child) => collectInline(child, marks, children))
    const textChildren = children.filter((c): c is BlockTextNode => c.type === 'text')
    if (textChildren.length) out.push({ type: 'link', url: el.getAttribute('href') || '', children: textChildren })
    return
  }
  const mark = MARK_TAGS[el.tagName]
  const nextMarks = mark ? { ...marks, [mark]: true } : marks
  el.childNodes.forEach((child) => collectInline(child, nextMarks, out))
}

const inlineChildren = (el: Node): BlockInlineNode[] => {
  const out: BlockInlineNode[] = []
  el.childNodes.forEach((child) => collectInline(child, {}, out))
  // Trim surrounding whitespace introduced by HTML formatting
  const first = out[0]
  if (first?.type === 'text') first.text = first.text.replace(/^\s+/, '')
  const last = out[out.length - 1]
  if (last?.type === 'text') last.text = last.text.replace(/\s+$/, '')
  const cleaned = out.filter((n) => n.type === 'link' || n.text !== '')
  // Strapi requires at least one child per block
  return cleaned.length ? cleaned : [{ type: 'text', text: '' }]
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TABLE', 'FIGURE'])

/**
 * Converts editor HTML to Strapi blocks so it can be saved in a `blocks` field.
 * Formatting Strapi blocks cannot represent (colours, tables, images) is reduced to text.
 * Returns null for empty content.
 */
export const htmlToBlocks = (html: string | null | undefined): StrapiBlockNode[] | null => {
  if (!html || !html.trim()) return null
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const blocks: StrapiBlockNode[] = []
  let looseInline: Node[] = []

  const flushLoose = () => {
    if (!looseInline.length) return
    const wrapper = doc.createElement('p')
    looseInline.forEach((n) => wrapper.appendChild(n.cloneNode(true)))
    looseInline = []
    if ((wrapper.textContent || '').trim()) blocks.push({ type: 'paragraph', children: inlineChildren(wrapper) })
  }

  const addBlock = (el: HTMLElement) => {
    const tag = el.tagName
    if (/^H[1-6]$/.test(tag)) {
      blocks.push({ type: 'heading', level: Number(tag[1]) as 1 | 2 | 3 | 4 | 5 | 6, children: inlineChildren(el) })
    } else if (tag === 'UL' || tag === 'OL') {
      const items = Array.from(el.children)
        .filter((li) => li.tagName === 'LI')
        .map((li) => ({ type: 'list-item' as const, children: inlineChildren(li) }))
      if (items.length) blocks.push({ type: 'list', format: tag === 'OL' ? 'ordered' : 'unordered', children: items })
    } else if (tag === 'BLOCKQUOTE') {
      blocks.push({ type: 'quote', children: inlineChildren(el) })
    } else if (tag === 'PRE') {
      blocks.push({ type: 'code', children: [{ type: 'text', text: el.textContent || '' }] })
    } else if (tag === 'DIV' && Array.from(el.children).some((c) => BLOCK_TAGS.has(c.tagName))) {
      el.childNodes.forEach((child) => visit(child))
      flushLoose()
    } else if ((el.textContent || '').trim()) {
      blocks.push({ type: 'paragraph', children: inlineChildren(el) })
    }
  }

  const visit = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as HTMLElement).tagName)) {
      flushLoose()
      addBlock(node as HTMLElement)
    } else if (node.nodeType === Node.ELEMENT_NODE || (node.textContent || '').trim()) {
      looseInline.push(node)
    }
  }

  doc.body.childNodes.forEach((node) => visit(node))
  flushLoose()
  return blocks.length ? blocks : null
}
